import type { Discount } from '@carvana/merch-contracts';
import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import { MoreDetailsModalSections } from '@carvana/merch-tile';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { AppliedFilters } from '~/contracts/common';
import { ModalId, ModalPriorities } from './constants';
import type { MoreDetailsOptions } from './types';

type ContextType = {
  filtersToSave: AppliedFilters;
  geicoDiscount: Discount | null;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isGeicoDiscountModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSampleTermsModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isShippingModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  setAddCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
  setGeicoDiscountModalOpen: (isOpen: boolean, discount?: Discount) => void;
  setIsSearchBarFocused: Dispatch<SetStateAction<boolean>>;
  setSaveSearchModalOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setSearchBarModalOpen: ModalQueueGeneratedOpener;
  setSoftPullModalOpen: ModalQueueGeneratedOpener;
  setSampleTermsModalOpen: ModalQueueGeneratedOpener;
  setShippingModalOpen: ModalQueueGeneratedOpener;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
};

const defaultContext: ContextType = {
  filtersToSave: {},
  geicoDiscount: null,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isGeicoDiscountModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSampleTermsModalOpen: false,
  isSaveSearchModalOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isShippingModalOpen: false,
  isSoftPullModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  setAddCoApplicantModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setGeicoDiscountModalOpen() {},
  setIsSearchBarFocused() {},
  setSaveSearchModalOpen() {},
  setSearchBarModalOpen() {},
  setSoftPullModalOpen() {},
  setSampleTermsModalOpen() {},
  setShippingModalOpen() {},
  setMoreDetailsModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener, modalQueue] =
    useModalQueue(ModalPriorities);
  const isMobile = useIsMobile();
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [geicoDiscount, setGeicoDiscount] = useState<Discount | null>(
    defaultContext.geicoDiscount
  );
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);

  const {
    setAddCoApplicantModalOpen,
    setAppraisalRefreshModalOpen,
    setGeicoDiscountModalOpen,
    setSaveSearchModalOpen,
    setSearchBarModalOpen,
    setSoftPullModalOpen,
    setSampleTermsModalOpen,
    setShippingModalOpen
  } = useMemo(() => {
    return {
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setGeicoDiscountModalOpen: generateOpener(ModalId.GeicoDiscount),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearch),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setSampleTermsModalOpen: generateOpener(ModalId.SampleTerms),
      setShippingModalOpen: generateOpener(ModalId.Shipping)
    };
  }, [generateOpener]);

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: AppliedFilters) => {
      setSaveSearchModalOpen(isOpen);
      setFiltersToSave(filters);
    },
    [setSaveSearchModalOpen]
  );

  const setGeicoDiscountModalOpenWithDiscount = useCallback<
    ContextType['setGeicoDiscountModalOpen']
  >(
    (isOpen: boolean, discount?: Discount) => {
      if (discount) setGeicoDiscount(discount);
      if (!isOpen) setGeicoDiscount(null);
      setGeicoDiscountModalOpen(isOpen);
    },
    [setGeicoDiscountModalOpen]
  );

  const setMoreDetailsModalOpenWithOptions = useCallback(
    (isOpen: boolean, options?: MoreDetailsOptions) => {
      generateOpener(ModalId.MoreDetails)(isOpen);
      if (options) {
        setMoreDetailsOptions(options);
      }
    },
    [generateOpener]
  );

  const value = useMemo<ContextType>((): ContextType => {
    return {
      filtersToSave,
      geicoDiscount,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isGeicoDiscountModalOpen: currentModalId === ModalId.GeicoDiscount,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearch,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isMoreDetailsModalOpen: isMobile
        ? modalQueue.has(ModalId.MoreDetails)
        : currentModalId === ModalId.MoreDetails,
      isSampleTermsModalOpen: currentModalId === ModalId.SampleTerms,
      isShippingModalOpen: currentModalId === ModalId.Shipping,
      moreDetailsOptions: moreDetailsOptions,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setGeicoDiscountModalOpen: setGeicoDiscountModalOpenWithDiscount,
      setIsSearchBarFocused,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setSampleTermsModalOpen,
      setShippingModalOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithOptions
    };
  }, [
    currentModalId,
    filtersToSave,
    geicoDiscount,
    isMobile,
    isSearchBarFocused,
    modalQueue,
    moreDetailsOptions,
    setAddCoApplicantModalOpen,
    setGeicoDiscountModalOpenWithDiscount,
    setSavedSearchModalOpenWithFiltersToSave,
    setSearchBarModalOpen,
    setSoftPullModalOpen,
    setAppraisalRefreshModalOpen,
    setSampleTermsModalOpen,
    setShippingModalOpen,
    setMoreDetailsModalOpenWithOptions
  ]);

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;
